// Compose: create or edit an image from several source images - a face from
// one, clothes from another, the scene from a third, or several photos of the
// same person for a better likeness. Runs on the gpuvm servers that take
// multiple references: Qwen-Image-2.1 Turbo, FLUX.2 [klein] 9B and FLUX.2-dev
// (t2imgFLUX2.py /edit with "images_base64").
//
// Each source image has a role (face, body, outfit, ...). The page keeps the
// images grouped by role, so their numbers are contiguous, and writes the
// sentence that tells the model what each image is for ("Images 1, 2 and 3 are
// photos of the same woman: keep the face ..."); the user describes the result.
import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { InputTextarea } from "primereact/inputtextarea";
import { InputNumber } from "primereact/inputnumber";
import { Checkbox } from "primereact/checkbox";
import { Slider } from "primereact/slider";
import { ProgressBar } from "primereact/progressbar";
import { Message } from "primereact/message";
import { Divider } from "primereact/divider";
import "primereact/resources/themes/lara-light-indigo/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import "./T2ImgChat.css";
import { imageApiBase, useServerLora, serverStillWorking } from "./imageApiUrl";
import SeedField from "./SeedField";

const GPU_API_URL = "http://195.230.127.226";
const MAX_REF_SIDE = 1024;          // px, longest side of each reference sent
const STALL_MS = 2 * 60 * 1000;     // no progress for this long after the first step = connection lost

// Servers that take several reference images. maxRefs matches MAX_REFERENCES
// in t2imgFLUX2.py; distilled models run fixed steps and no guidance.
const MODELS = [
  { port: 8516, label: "Qwen-Image-2.1 Turbo (best likeness, up to 6 images)", short: "Qwen 2.1",
    maxRefs: 6, steps: 8, guidance: 1.0, tunable: false, time: "~2-3 min" },
  { port: 8512, label: "FLUX.2 [klein] 9B (fast, up to 4 images)", short: "klein",
    maxRefs: 4, steps: 4, guidance: 1.0, tunable: false, time: "~1 min" },
  { port: 8510, label: "FLUX.2-dev (most detail, slow, up to 4 images)", short: "FLUX.2",
    maxRefs: 4, steps: 28, guidance: 4.0, tunable: true, time: "~5-10 min" },
];

// In this order the images are numbered; "other" adds nothing to the prompt.
const ROLES = [
  { value: "face", label: "Face" },
  { value: "body", label: "Body / figure" },
  { value: "outfit", label: "Outfit" },
  { value: "scene", label: "Scene / background" },
  { value: "style", label: "Style" },
  { value: "object", label: "Object" },
  { value: "other", label: "Other (say it in the prompt)" },
];
const ROLE_ORDER = Object.fromEntries(ROLES.map((r, i) => [r.value, i]));
// Array.sort is stable, so images keep their order within a role
const sortByRole = (list) => [...list].sort((a, b) => ROLE_ORDER[a.role] - ROLE_ORDER[b.role]);

const SUBJECTS = ["person", "woman", "man"].map((s) => ({ label: s, value: s }));

const SIZES = [
  { label: "Auto (shape of the last image)", value: null },
  { label: "Square 1024 x 1024", value: [1024, 1024] },
  { label: "Portrait 768 x 1024", value: [768, 1024] },
  { label: "Landscape 1024 x 768", value: [1024, 768] },
  { label: "Wide 1344 x 768", value: [1344, 768] },
];

const EXAMPLES = (who) => [
  { label: "Full body, new scene",
    text: `A full-body photo of the ${who} walking along a sunny beach at golden hour, natural smile, photorealistic` },
  { label: "Portrait",
    text: `A close-up portrait of the ${who} in soft window light, looking at the camera, photorealistic` },
  { label: "Outfit in the scene",
    text: `The ${who} wearing the outfit, standing in the scene, full-body photo, natural pose, photorealistic` },
  { label: "Product in the scene",
    text: "Place the object on the table in the scene, matching the lighting of the scene" },
];

// "Image 1" / "Images 1 and 2" / "Images 1, 2 and 3" (lower case inside a sentence)
const numberList = (nums, capital = true) => {
  const word = `${capital ? "I" : "i"}mage${nums.length > 1 ? "s" : ""}`;
  return nums.length === 1 ? `${word} ${nums[0]}`
    : `${word} ${nums.slice(0, -1).join(", ")} and ${nums[nums.length - 1]}`;
};
const POSSESSIVE = { woman: "her", man: "his", person: "their" };

// The sentences that tell the model what each (grouped) image is for. Several
// face/body photos must be stated to be ONE person: otherwise the model reads
// three body photos as three people and draws them all.
const buildGuide = (refs, who) => {
  const numbersOf = (...roles) => refs.map((r, i) => (roles.includes(r.role) ? i + 1 : null)).filter(Boolean);
  const say = (nums, one, many) => `${numberList(nums)} ${nums.length > 1 ? many : one}`;
  const their = POSSESSIVE[who] || "their";
  const parts = [];
  const face = numbersOf("face");
  const body = numbersOf("body");
  const subject = numbersOf("face", "body");
  const outfit = numbersOf("outfit");
  const scene = numbersOf("scene");
  const style = numbersOf("style");
  const object = numbersOf("object");
  if (subject.length > 1) {
    parts.push(`${numberList(subject)} ${subject.length === 2 ? "both" : "all"} show one and the same ${who}.`);
    const keep = [];
    if (face.length) keep.push(`${their} face and identity exactly as in ${numberList(face, false)}`);
    if (body.length) keep.push(`${their} body shape, proportions and figure as in ${numberList(body, false)}`);
    parts.push(`Keep ${keep.join(", and ")}.`);
    parts.push(`The new image shows only this one ${who}.`);
  } else if (face.length) {
    parts.push(`${numberList(face)} shows the ${who}: keep ${their} face and identity exactly.`);
  } else if (body.length) {
    parts.push(`${numberList(body)} shows the ${who}: keep ${their} body shape, proportions and figure.`);
  }
  if (outfit.length) parts.push(say(outfit, "shows", "show") + ` the clothes the ${who} should wear.`);
  if (scene.length) parts.push(say(scene, "shows", "show") + " the scene and background to use.");
  if (style.length) parts.push(say(style, "shows", "show") + " the visual style to follow.");
  if (object.length) parts.push(say(object, "shows an object", "show objects") + " to include.");
  return parts.join(" ");
};

let nextId = 1;

const ImageComposeChat = () => {
  const [port, setPort] = useState(MODELS[0].port);
  const [refs, setRefs] = useState([]);            // [{id, url, name, role}], grouped by role
  const [who, setWho] = useState("person");
  const [autoGuide, setAutoGuide] = useState(true);
  const [prompt, setPrompt] = useState("");
  const [size, setSize] = useState(null);
  const [seed, setSeed] = useState(null);
  const [lastSeed, setLastSeed] = useState(null);   // the seed the server used for the last image
  const [steps, setSteps] = useState(MODELS[0].steps);
  const [guidance, setGuidance] = useState(MODELS[0].guidance);
  const [loraWeight, setLoraWeight] = useState(1.0);
  const [result, setResult] = useState(null);      // object URL of the composed image
  const [isRunning, setIsRunning] = useState(false);
  const [loadingModel, setLoadingModel] = useState(false);
  const [progress, setProgress] = useState(0);
  const [eta, setEta] = useState("");
  const [error, setError] = useState("");
  const [dragOver, setDragOver] = useState(false);

  const wsRef = useRef(null);
  const cancelledRef = useRef(false);
  const lastProgressRef = useRef(0);
  const fileInputRef = useRef(null);

  const model = MODELS.find((m) => m.port === port);
  const loraFile = useServerLora(GPU_API_URL, port);
  const base = imageApiBase(GPU_API_URL, port);
  const guide = autoGuide ? buildGuide(refs, who) : "";
  const fullPrompt = [guide, prompt.trim()].filter(Boolean).join(" ");

  // Revoke object URLs on unmount
  const urlsRef = useRef({ refs, result });
  urlsRef.current = { refs, result };
  useEffect(() => () => {
    cleanupWebSocket();
    urlsRef.current.refs.forEach((r) => URL.revokeObjectURL(r.url));
    if (urlsRef.current.result) URL.revokeObjectURL(urlsRef.current.result);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Stall watchdog (as on the other image pages): once steps are reported they
  // arrive every ~10-15 s, so 2 min of silence means the socket died silently.
  useEffect(() => {
    if (!isRunning) return undefined;
    // A silent stretch is checked with the server first (see serverStillWorking):
    // while it is still busy the job goes on and the clock restarts.
    let checking = false;
    const id = setInterval(async () => {
      const last = lastProgressRef.current;
      if (checking || !last || Date.now() - last <= STALL_MS) return;
      checking = true;
      const ws = wsRef.current;
      const working = await serverStillWorking(base);
      checking = false;
      // Held-back messages may have finished or ended the job meanwhile
      if (wsRef.current !== ws || !lastProgressRef.current) return;
      if (working) {
        lastProgressRef.current = Date.now();
        return;
      }
      setError("No progress from the image server for 2 minutes and it is no longer working on the job - the connection was lost. Please try again.");
      cleanupWebSocket();
      setIsRunning(false);
    }, 10000);
    return () => clearInterval(id);
  }, [isRunning]); // eslint-disable-line react-hooks/exhaustive-deps

  const cleanupWebSocket = () => {
    lastProgressRef.current = 0;
    setLoadingModel(false);
    const ws = wsRef.current;
    if (ws) {
      ws.onopen = ws.onmessage = ws.onerror = ws.onclose = null;
      if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) ws.close();
      wsRef.current = null;
    }
  };

  // ---- source images ----
  // New images take the role of the last one added (first one: face), so adding
  // three face photos and then switching to body for the next ones is quick.
  const addFiles = (files) => {
    const images = Array.from(files || []).filter((f) => f.type.startsWith("image/"));
    if (!images.length) return;
    setRefs((cur) => {
      const room = model.maxRefs - cur.length;
      if (images.length > room) {
        setError(`${model.short} takes at most ${model.maxRefs} images; ${images.length - Math.max(room, 0)} not added.`);
      } else {
        setError("");
      }
      const role = cur.length ? cur[cur.length - 1].role : "face";
      const added = images.slice(0, Math.max(room, 0))
        .map((f) => ({ id: nextId++, url: URL.createObjectURL(f), name: f.name, role }));
      return sortByRole([...cur, ...added]);
    });
  };

  const removeRef = (id) => setRefs((cur) => {
    const r = cur.find((x) => x.id === id);
    if (r) URL.revokeObjectURL(r.url);
    return cur.filter((x) => x.id !== id);
  });

  const setRole = (id, role) => setRefs((cur) => sortByRole(cur.map((r) => (r.id === id ? { ...r, role } : r))));

  // Reorder within a role group (the groups themselves follow ROLES)
  const moveRef = (index, delta) => setRefs((cur) => {
    const to = index + delta;
    if (to < 0 || to >= cur.length || cur[to].role !== cur[index].role) return cur;
    const next = [...cur];
    [next[index], next[to]] = [next[to], next[index]];
    return next;
  });

  // The composed image as a new source image (e.g. refine it further)
  const useResultAsReference = async () => {
    if (!result) return;
    if (refs.length >= model.maxRefs) {
      setError(`${model.short} takes at most ${model.maxRefs} images; remove one first.`);
      return;
    }
    const blob = await (await fetch(result)).blob();
    setRefs((cur) => sortByRole([...cur, { id: nextId++, url: URL.createObjectURL(blob), name: "composed result", role: "other" }]));
  };

  const selectModel = (p) => {
    const m = MODELS.find((x) => x.port === p);
    setPort(p);
    setSteps(m.steps);
    setGuidance(m.guidance);
    if (refs.length > m.maxRefs) {
      setError(`${m.short} takes at most ${m.maxRefs} images: remove ${refs.length - m.maxRefs} before composing.`);
    } else {
      setError("");
    }
  };

  // Scale to MAX_REF_SIDE and send as JPEG: keeps the request small (the server's
  // WebSocket message limit) and the models work at ~1 MP per reference anyway.
  const toBase64 = (url) => new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, MAX_REF_SIDE / Math.max(img.width, img.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff";   // flatten transparency onto white
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL("image/jpeg", 0.92).split(",")[1]);
    };
    img.onerror = reject;
    img.src = url;
  });

  // ---- compose ----
  const fetchResult = async (imageId) => {
    try {
      const response = await axios.post(`${base}/generate-image`, { image_id: imageId },
        { responseType: "blob", timeout: 30000 });
      if (cancelledRef.current) return;
      if (result) URL.revokeObjectURL(result);
      setResult(URL.createObjectURL(response.data));
      setProgress(100);
    } catch (err) {
      if (!cancelledRef.current) setError("Failed to fetch the composed image");
    } finally {
      if (!cancelledRef.current) setIsRunning(false);
    }
  };

  const handleMessage = (event) => {
    let payload;
    try {
      payload = JSON.parse(event.data);
    } catch (e) {
      payload = { type: "text", message: event.data };
    }
    if (payload.type === "progress") {
      lastProgressRef.current = Date.now();
      setLoadingModel(false);
      setProgress(Number(payload.progress) || 0);
      setEta(payload.eta_str || "");
    } else if (payload.type === "complete") {
      cleanupWebSocket();
      setLastSeed(payload.seed ?? null);
      fetchResult(payload.image_id);
    } else if (payload.type === "error") {
      setError(`Error: ${payload.message || "unknown"}`);
      cleanupWebSocket();
      setIsRunning(false);
    } else if (payload.type === "text" && typeof payload.message === "string") {
      setLoadingModel(/loading .*model/i.test(payload.message));
    }
  };

  const handleCompose = async () => {
    if (refs.length === 0) return setError("Add at least one image");
    if (refs.length > model.maxRefs) return setError(`${model.short} takes at most ${model.maxRefs} images`);
    if (!prompt.trim()) return setError("Describe the image to create");

    setError("");
    setIsRunning(true);
    setProgress(0);
    setEta("");
    cancelledRef.current = false;
    cleanupWebSocket();

    let images;
    try {
      images = await Promise.all(refs.map((r) => toBase64(r.url)));
    } catch (e) {
      setError("Failed to read one of the images");
      setIsRunning(false);
      return;
    }
    if (cancelledRef.current) return;

    const ws = new WebSocket(`${base.replace(/^http/, "ws")}/edit`);
    wsRef.current = ws;
    ws.onopen = () => {
      if (cancelledRef.current) return;
      const body = {
        images_base64: images,
        prompt: fullPrompt,
        num_inference_steps: steps,
        guidance_scale: model.tunable ? guidance : model.guidance,
        lora_weight: loraWeight,
      };
      if (size) [body.width, body.height] = size;
      if (seed !== null && seed !== undefined) body.seed = seed;
      ws.send(JSON.stringify(body));
    };
    ws.onmessage = (event) => {
      if (!cancelledRef.current) handleMessage(event);
    };
    ws.onerror = () => {
      if (cancelledRef.current) return;
      setError(`Failed to connect to ${model.short} (${port})`);
      cleanupWebSocket();
      setIsRunning(false);
    };
    // complete/error detach this handler first, so a close here means the
    // connection dropped mid-job
    ws.onclose = (event) => {
      if (wsRef.current === ws) wsRef.current = null;
      if (cancelledRef.current) return;
      setError(`Connection to the image server was lost (code ${event.code}). Please try again.`);
      cleanupWebSocket();
      setIsRunning(false);
    };
  };

  const handleCancel = () => {
    cancelledRef.current = true;
    cleanupWebSocket();
    setIsRunning(false);
    setProgress(0);
    setEta("");
    setError("Cancelled - the server stops the image within a few seconds");
  };

  const download = () => {
    if (!result) return;
    const a = document.createElement("a");
    a.href = result;
    a.download = `composed_${Date.now()}.png`;
    a.click();
  };

  // ---- UI ----
  const runningLabel = loadingModel
    ? "Loading model... (a few minutes after it was idle)"
    : progress === 0
      ? `Preparing (${model.short}): reading the prompt and ${refs.length} image${refs.length === 1 ? "" : "s"}...`
      : `Composing (${model.short})... ${progress}%${eta && eta !== "00:00" ? ` (ETA ${eta})` : ""}`;

  const refGrid = (
    <div
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => { e.preventDefault(); setDragOver(false); if (!isRunning) addFiles(e.dataTransfer.files); }}
      style={{
        border: `2px dashed ${dragOver ? "#6366f1" : "#cbd5e1"}`, borderRadius: 8, padding: 12,
        background: dragOver ? "#eef2ff" : "transparent",
      }}
    >
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(130px, 1fr))", gap: 12 }}>
        {refs.map((r, i) => (
          <div key={r.id} style={{ border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden", background: "#fff" }}>
            <div style={{ position: "relative" }}>
              <img src={r.url} alt={`Source ${i + 1}: ${r.name}`}
                   style={{ width: "100%", height: 120, objectFit: "cover", display: "block" }} />
              <span style={{ position: "absolute", top: 4, left: 4, background: "rgba(17,24,39,.8)", color: "#fff",
                             borderRadius: 4, padding: "1px 6px", fontSize: 12, fontWeight: 600 }}>
                image {i + 1}
              </span>
            </div>
            <select value={r.role} disabled={isRunning} onChange={(e) => setRole(r.id, e.target.value)}
                    aria-label={`Role of image ${i + 1}`}
                    style={{ width: "100%", border: "none", borderTop: "1px solid #e5e7eb", padding: "4px 6px",
                             fontSize: 13, background: "#f8fafc", color: "#1f2937" }}>
              {ROLES.map((ro) => <option key={ro.value} value={ro.value}>{ro.label}</option>)}
            </select>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: 2 }}>
              <Button icon="pi pi-arrow-left" className="p-button-text p-button-sm" aria-label="Move earlier"
                      disabled={isRunning || i === 0 || refs[i - 1].role !== r.role} onClick={() => moveRef(i, -1)} />
              <Button icon="pi pi-times" className="p-button-text p-button-danger p-button-sm" disabled={isRunning}
                      onClick={() => removeRef(r.id)} aria-label="Remove" />
              <Button icon="pi pi-arrow-right" className="p-button-text p-button-sm" aria-label="Move later"
                      disabled={isRunning || i === refs.length - 1 || refs[i + 1].role !== r.role}
                      onClick={() => moveRef(i, 1)} />
            </div>
          </div>
        ))}
        {refs.length < model.maxRefs && (
          <button type="button" disabled={isRunning} onClick={() => fileInputRef.current && fileInputRef.current.click()}
                  style={{ minHeight: 182, border: "1px dashed #94a3b8", borderRadius: 8, background: "#f8fafc",
                           color: "#475569", cursor: isRunning ? "default" : "pointer", fontSize: 14 }}>
            <i className="pi pi-plus" style={{ display: "block", fontSize: 20, marginBottom: 6 }} />
            Add image{refs.length ? "" : "s"}
            <div style={{ fontSize: 12, marginTop: 4 }}>or drop here</div>
          </button>
        )}
      </div>
      <input ref={fileInputRef} type="file" accept="image/*" multiple style={{ display: "none" }}
             onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
      <small style={{ display: "block", marginTop: 8, color: "#64748b" }}>
        {refs.length}/{model.maxRefs} images. Pick each image's role; images stay grouped by role.
        For faces, crop tightly to the face: with several images each one is sent smaller.
      </small>
    </div>
  );

  return (
    <div className="p-grid p-justify-center p-align-start p-mt-4" style={{ padding: "1rem" }}>
      <div className="p-col-12 p-md-6 image-panel">
        <Card title="Composed Image" style={{ height: "100%" }}>
          <div style={{ minHeight: 320, display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center" }}>
            {result ? (
              <img src={result} alt="Composed"
                   style={{ maxWidth: "100%", maxHeight: 560, borderRadius: 8, boxShadow: "0 4px 6px rgba(0,0,0,.1)" }} />
            ) : (
              <div style={{ color: "#6b7280" }}>
                <i className="pi pi-images" style={{ fontSize: "3rem", display: "block", marginBottom: "1rem" }} />
                <p>Add your source images, describe the result and press Compose</p>
              </div>
            )}
          </div>
          {result && (
            <>
              <Divider />
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <Button label="Download PNG" icon="pi pi-download" className="p-button-success" onClick={download} />
                <Button label="Use as a source image" icon="pi pi-replay" className="p-button-outlined"
                        onClick={useResultAsReference} disabled={isRunning} />
              </div>
            </>
          )}
        </Card>
      </div>

      <div className="p-col-12 p-md-6 controls-panel">
        <Card title={`Compose from Several Images - ${model.short}`}>
          <div className="p-fluid">
            {error && <Message severity="error" text={error} style={{ marginBottom: "1rem" }} />}

            <div className="p-field">
              <label htmlFor="composeModel">Model</label>
              <Dropdown id="composeModel" value={port} onChange={(e) => selectModel(e.value)} disabled={isRunning}
                        options={MODELS.map((m) => ({ label: m.label, value: m.port }))} />
              <small>One image takes {model.time} on the V100.</small>
            </div>

            <div className="p-field">
              <label>Source images</label>
              {refGrid}
            </div>

            <div className="p-grid" style={{ gap: "1rem", alignItems: "flex-end" }}>
              <div className="p-col-6">
                <div className="p-field">
                  <label htmlFor="composeWho">The subject is a</label>
                  <Dropdown id="composeWho" value={who} options={SUBJECTS} onChange={(e) => setWho(e.value)} disabled={isRunning} />
                </div>
              </div>
              <div className="p-col-6">
                <div className="p-field" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Checkbox inputId="composeGuide" checked={autoGuide} onChange={(e) => setAutoGuide(e.checked)}
                            disabled={isRunning} />
                  <label htmlFor="composeGuide" style={{ marginBottom: 0, fontWeight: 400 }}>
                    Describe the images in the prompt automatically
                  </label>
                </div>
              </div>
            </div>

            <div className="p-field">
              <label htmlFor="composePrompt">What to create</label>
              <InputTextarea id="composePrompt" value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={3}
                             autoResize disabled={isRunning}
                             placeholder={autoGuide
                               ? `e.g. A full-body photo of the ${who} walking along a beach at sunset, photorealistic`
                               : "e.g. The woman from image 1 wearing the jacket from image 2, on the street from image 3, photorealistic"} />
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
                {EXAMPLES(who).map((ex) => (
                  <Button key={ex.label} label={ex.label} className="p-button-outlined p-button-sm" style={{ width: "auto" }}
                          disabled={isRunning} onClick={() => setPrompt(ex.text)} />
                ))}
              </div>
              {autoGuide ? (
                guide && (
                  <div style={{ marginTop: 10, padding: "8px 10px", background: "#f1f5f9", borderRadius: 6,
                                fontSize: 13, color: "#334155" }}>
                    <b>Sent to the model:</b> {fullPrompt || guide}
                  </div>
                )
              ) : (
                <small>Call the pictures image 1, image 2, ... and say what to take from each.</small>
              )}
            </div>

            <div className="p-grid" style={{ gap: "1rem" }}>
              <div className="p-col-6">
                <div className="p-field">
                  <label htmlFor="composeSize">Output size</label>
                  <Dropdown id="composeSize" value={size} options={SIZES} onChange={(e) => setSize(e.value)} disabled={isRunning} />
                </div>
              </div>
              <div className="p-col-6">
                <SeedField id="composeSeed" value={seed} onChange={setSeed} lastSeed={lastSeed} disabled={isRunning} />
              </div>
            </div>

            {model.tunable && (
              <div className="p-grid" style={{ gap: "1rem" }}>
                <div className="p-col-6">
                  <div className="p-field">
                    <label htmlFor="composeSteps">Inference steps</label>
                    <InputNumber id="composeSteps" value={steps} onValueChange={(e) => setSteps(e.value)} min={10} max={50}
                                 showButtons disabled={isRunning} />
                  </div>
                </div>
                <div className="p-col-6">
                  <div className="p-field">
                    <label htmlFor="composeGuidance">Guidance scale</label>
                    <InputNumber id="composeGuidance" value={guidance} onValueChange={(e) => setGuidance(e.value)}
                                 min={1} max={10} step={0.5} minFractionDigits={1} showButtons disabled={isRunning} />
                  </div>
                </div>
              </div>
            )}

            {loraFile && (
              <div className="p-field">
                <label htmlFor="composeLora">LoRA strength ({loraFile}): {loraWeight.toFixed(2)}</label>
                <Slider id="composeLora" value={loraWeight} onChange={(e) => setLoraWeight(e.value)} min={0} max={1.5}
                        step={0.05} disabled={isRunning} />
                <small>0 = LoRA off, 1 = full strength</small>
              </div>
            )}

            {isRunning ? (
              <>
                <ProgressBar value={progress} style={{ height: "1.5rem", marginBottom: "1rem" }} />
                <div style={{ display: "flex", gap: 8 }}>
                  <Button label={runningLabel} icon="pi pi-spin pi-spinner" className="p-button-lg" disabled style={{ flex: 1 }} />
                  <Button label="Cancel" icon="pi pi-times" className="p-button-danger p-button-lg" onClick={handleCancel}
                          style={{ width: "auto" }} />
                </div>
              </>
            ) : (
              <Button label={`Compose (${model.short})`} icon="pi pi-images" className="p-button-lg"
                      onClick={handleCompose} disabled={refs.length === 0 || refs.length > model.maxRefs} />
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default ImageComposeChat;
